import { useCallback, useEffect, useRef, useState } from "react";
import { useT } from "../../lib/i18n";
import { useDismissOnOutside } from "./Overlay";

/**
 * 可搜索的下拉，单选与多选两种模式。
 *
 * 原生 `<select>` 在选项上百条时只能靠滚 —— 地区表有 280 条，
 * 而它自带的输入跳转对中文输入法不可靠（IME 组合期间收不到按键）。
 *
 * 面板用 **fixed**：调用它的抽屉/表格容器带 `overflow-auto`，
 * `absolute` 面板会被裁成一条缝。收起时机交给 `useDismissOnOutside`
 * ——包括「面板自己滚动时不能收起」那条，理由见那个 hook。
 *
 * 面板与行的样式对齐仓库既有的两个下拉（`I18nGrid` 的语言选择、`TopBar` 的代理选择）：
 * `border-line-lighter` + `p-1` + 行 `rounded-sm px-2.5 py-1.5`。
 *
 * ## 跟 `MultiSelect` 的分工
 *
 * `MultiSelect` 是**表格单元格里**的紧凑控件（h-8 / text-xs / 固定窄宽），选项是配好的
 * 一小撮，没有搜索。这里是**表单里**的控件，选项可以上千、可以来自服务端。
 * 两者的尺寸和用法都不一样，合成一个只会让两边都别扭。
 */
export interface SearchOption {
  /** 存进数据的值 —— 显示名会随多语言变，不能拿它当值 */
  value: string;
  label: string;
  /** 额外的匹配词（比如地区码 JP），参与搜索并作为行尾的次要信息 */
  keywords?: string;
}

interface CommonProps {
  options: SearchOption[];
  /**
   * 给了就表示 **options 由调用方按关键词去服务端取**，组件不再自己过滤。
   *
   * 不关掉自带过滤的话会过滤两次，而两次的口径不一样——服务端可能按昵称和 id
   * 一起匹配，这里只认 `label`/`keywords`，于是服务端明明搜到了、列表里却不显示。
   */
  onQueryChange?: (q: string) => void;
  placeholder?: string;
  disabled?: boolean;
  width?: number;
  /** 列表底部的一行说明（「还有 N 条，打细一点」这种） */
  hint?: string;
  /**
   * 取选项时出错了的原话。**必须和「确实没搜到」分开画。**
   *
   * 两者都是空列表，但一个是「这个人不存在」、另一个是「我没问到」。画成同一句
   * 「没有匹配」的话，运营会把后者当成前者：搜第三个人时接口抖了一下，
   * 他判断「这号注销了」，按两个人发出去——而邮件发出去补不回来。
   */
  error?: string | null;
  /** 正在取。用它把「还没回来」和「回来了但是空的」分开 */
  loading?: boolean;
  /** 调用方存着的 id → 显示名。组件重新挂载时靠它把标签认回来 */
  knownLabels?: Record<string, string>;
}

type Props = CommonProps & (
  | {
    multiple?: false;
    value: string;
    onChange: (v: string) => void;
    /** 「不选」那一项的文案；不给则不允许清空 */
    emptyLabel?: string;
  }
  | {
    multiple: true;
    value: string[];
    /**
     * 第二个参数是**这次操作之后每一项的显示名**。
     *
     * 调用方只存 id 的话，组件一卸载（比如切走再切回来）内部那份 label 缓存就没了，
     * 标签全退成裸 id——而这个组件存在的理由正是「让人看得见名字再决定」。
     * 想留住名字的调用方接住它，不想留的忽略即可。
     */
    onChange: (v: string[], labels: Record<string, string>) => void;
    emptyLabel?: never;
    /**
     * 允许把搜索框里打的字直接加成一项（值 = 显示名 = 去首尾空白后的原文）。
     * 用在「候选项就是以前填过的值」的自由标签上：选项里没有一模一样的，列表顶上多一行「添加」，
     * 回车也是添加；有一模一样的，回车选中它而不是再造一个
     */
    creatable?: boolean;
  }
);

export function SearchSelect(props: Props) {
  const { options, onQueryChange, placeholder, disabled, width = 220, hint, error, loading, knownLabels } = props;
  const multiple = props.multiple === true;
  const picked = multiple ? props.value : props.value ? [props.value] : [];

  const t = useT();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [pos, setPos] = useState<{ left: number; top: number; width: number } | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  /*
   * 已选项的显示名缓存。
   *
   * `options` 只装着**当前这次搜索**的结果：搜「小王」选中，再搜「小李」，
   * 上一个人就不在 options 里了。不缓存的话已选标签会退成一串 id，
   * 而运营挑人时看的就是昵称——退成 id 等于把刚做的判断作废。
   */
  const labels = useRef(new Map<string, string>());
  useEffect(() => {
    for (const o of options) labels.current.set(o.value, o.label);
  }, [options]);
  // 调用方记下来的名字也并回来：组件卸载重挂之后缓存是空的，靠这个才认得出老选项
  useEffect(() => {
    for (const [k, v] of Object.entries(knownLabels ?? {})) if (v) labels.current.set(k, v);
  }, [knownLabels]);
  // 先看这一次渲染手上的 options / knownLabels：缓存是在 effect 里填的，首次渲染时还是空的——
  // 只看缓存的话，首屏（尤其是禁用态，之后不会再重渲染）显示的是裸编号而不是名字
  const labelOf = (v: string) => options.find((o) => o.value === v)?.label ?? knownLabels?.[v] ?? labels.current.get(v) ?? v;

  const close = useCallback(() => setOpen(false), []);
  useDismissOnOutside(open, { box, panel }, close);

  const kw = q.trim().toLowerCase();
  // 名字和 keywords 都参与匹配：运营既可能打「日本」也可能打「JP」
  const hits = onQueryChange || !kw
    ? options
    : options.filter((o) => o.label.toLowerCase().includes(kw) || (o.keywords ?? "").toLowerCase().includes(kw));
  const typed = q.trim();
  // 精确等于某个已有选项（或已选项）就不再提供「添加」：同一个标签出现两份，下拉和筛选都会分裂
  const exact = typed ? options.find((o) => o.value === typed || o.label === typed)?.value ?? (picked.includes(typed) ? typed : null) : null;
  const canCreate = props.multiple === true && props.creatable === true && !!typed && exact == null;

  const pick = (v: string) => {
    if (props.multiple === true) {
      const has = props.value.includes(v);
      const next = has ? props.value.filter((x) => x !== v) : [...props.value, v];
      props.onChange(next, Object.fromEntries(next.map((id) => [id, labelOf(id)])));
      // 多选不收起：连着挑几个是常态，每选一个都要重开等于每次都重打一遍关键词
      return;
    }
    props.onChange(v);
    setOpen(false);
    setQ("");
  };

  const triggerText = multiple
    ? (picked.length ? t("common.pickedN", { n: picked.length }) : null)
    : (picked.length ? labelOf(picked[0]) : null);

  return (
    <div className="flex flex-col gap-1.5" style={{ width: multiple ? undefined : width }}>
      <div ref={box} className="relative" style={{ width }}>
        <button
          type="button" disabled={disabled}
          onClick={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            // 面板至少和触发器一样宽；名字可以很长（阿拉伯联合酋长国），给它加宽的余地
            setPos({ left: r.left, top: r.bottom + 4, width: Math.max(r.width, 240) });
            setQ("");
            onQueryChange?.("");
            setOpen((v) => !v);
          }}
          className={
            "flex h-control w-full items-center justify-between gap-1 rounded-base border bg-white px-2 text-left text-base outline-none " +
            (disabled
              ? "cursor-not-allowed border-line bg-fill-light text-ink-disabled"
              : `text-ink hover:border-primary ${open ? "border-primary" : "border-line"}`)
          }
        >
          <span className="truncate">
            {triggerText ?? (
              <span className="text-ink-placeholder">
                {(props.multiple === true ? undefined : props.emptyLabel) ?? placeholder ?? ""}
              </span>
            )}
          </span>
          <span className={`flex-none text-[10px] text-ink-placeholder ${open ? "rotate-180" : ""}`}>▾</span>
        </button>

        {open && !disabled && pos && (
          <div
            ref={panel}
            className="fixed z-[70] flex max-h-[320px] flex-col overflow-hidden rounded-base border border-line-lighter bg-white shadow-light"
            style={{ left: pos.left, top: pos.top, width: pos.width }}
          >
            {/* 搜索框与列表之间用一条分隔线，不用带边框的浮块——那看着像两个控件 */}
            <div className="flex-none border-b border-line-lighter p-1">
              <input
                autoFocus
                value={q}
                placeholder={placeholder ?? t("common.search")}
                onChange={(e) => { setQ(e.target.value); onQueryChange?.(e.target.value); }}
                onKeyDown={(e) => {
                  if (e.key === "Escape") { setOpen(false); return; }
                  // 中文输入法选字时的回车不是「确认」：不挡的话拼音还没上屏就被当成一项加进去了
                  if (e.nativeEvent.isComposing) return;
                  if (props.multiple === true && props.creatable === true && e.key === "Enter" && typed) {
                    e.preventDefault();
                    // 已选的精确项回车不取消：打字回车的意思是「要它」，toggle 掉会让人以为没加上
                    if (exact == null) pick(typed);
                    else if (!picked.includes(exact)) pick(exact);
                    setQ("");
                    return;
                  }
                  // 敲回车直接取第一条命中：搜完还要伸手去点，搜索框就只做了一半
                  /*
                   * 还在取的时候回车**什么都不做**。
                   *
                   * options 在新结果回来之前一直是上一次那一页，而多选模式下 pick 是
                   * toggle——「粘 A 回车、粘 B 回车」打字快一点，第二次回车取到的
                   * hits[0] 还是 A，于是把刚选中的 A 又删掉，B 一个没加。
                   * 标签行少一个人，没有任何报错。
                   */
                  if (e.key === "Enter" && hits.length && !loading && !error) { e.preventDefault(); pick(hits[0].value); }
                }}
                className="h-control-sm w-full rounded-sm px-2 text-sm text-ink outline-none placeholder:text-ink-placeholder"
              />
            </div>
            {/* min-h-0 不能省：flex 列里的滚动子项默认按内容撑高，会把 max-h 顶穿 */}
            <div className="min-h-0 flex-1 overflow-y-auto p-1">
              {canCreate && (
                <div onClick={() => { pick(typed); setQ(""); }}
                  className="flex cursor-pointer items-center gap-2 rounded-sm px-2.5 py-1.5 text-sm text-primary hover:bg-fill-light">
                  <span className="w-3 flex-none">+</span>
                  <span className="flex-1 truncate">{t("common.createOption", { q: typed })}</span>
                </div>
              )}
              {props.multiple !== true && props.emptyLabel && !kw && (
                <div onClick={() => pick("")}
                  className={
                    "cursor-pointer rounded-sm px-2.5 py-1.5 text-sm hover:bg-fill-light " +
                    (props.value ? "text-ink-placeholder" : "bg-primary-50 font-semibold text-primary")
                  }>
                  {props.emptyLabel}
                </div>
              )}
              {!error && !loading && hits.map((o) => {
                const on = picked.includes(o.value);
                return (
                  <div
                    key={o.value}
                    onClick={() => pick(o.value)}
                    className={
                      "flex cursor-pointer items-center gap-2 rounded-sm px-2.5 py-1.5 text-sm hover:bg-fill-light " +
                      (on ? "bg-primary-50 font-semibold text-primary" : "text-ink-regular")
                    }
                  >
                    {/* 多选给个勾：面板不收起，不标出来就看不出刚才那下点中没有 */}
                    {multiple && <span className="w-3 flex-none text-primary">{on ? "✓" : ""}</span>}
                    <span className="flex-1 truncate">{o.label}</span>
                    {o.keywords && <span className="flex-none font-mono text-xs text-ink-placeholder">{o.keywords}</span>}
                  </div>
                );
              })}
              {error ? (
                <div className="px-2.5 py-2 text-sm leading-[1.6] text-danger">
                  {t("common.searchFailed")}
                  <div className="mt-0.5 break-all text-xs text-ink-placeholder">{error}</div>
                </div>
              ) : loading ? (
                <div className="px-2.5 py-2 text-sm text-ink-placeholder">{t("common.searching")}</div>
              ) : !hits.length && !canCreate ? (
                <div className="px-2.5 py-2 text-sm text-ink-placeholder">{t("common.noMatch", { q: q.trim() })}</div>
              ) : null}
            </div>
            {hint && (
              <div className="flex-none border-t border-line-lighter px-2.5 py-1.5 text-xs text-ink-placeholder">{hint}</div>
            )}
          </div>
        )}
      </div>

      {/*
        * 已选项摊在控件下面，不塞进触发器里。
        *
        * 塞进去的话选到第四个就开始截断，运营看不全自己选了谁；而这个组件的多选
        * 场景（发邮件挑收件人）恰恰是「选完要核对一遍」的。
        */}
      {multiple && picked.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {picked.map((v) => (
            <span key={v} className="flex items-center gap-1 rounded-base bg-fill-light px-2 py-0.5 text-sm text-ink-regular">
              <span className="max-w-[180px] truncate" title={v}>{labelOf(v)}</span>
              <button
                type="button"
                disabled={disabled}
                onClick={() => pick(v)}
                className="flex-none text-ink-placeholder hover:text-danger disabled:cursor-not-allowed"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
